import ApiService from './ApiService';
import { ENV } from '../environments/environment';

export interface GradingFormField {
  field_id: number;
  field_key: string;
  value: any;
}

export interface GradingSubmission {
  protocol_id: number;
  study_instance_uid?: string;
  series_instance_uid?: string;
  instance_uid?: string;
  patient_id?: string;
  grader_id?: number;
  grading_date?: string;
  form_data: Record<string, any>;
  fields?: GradingFormField[];
  status?: 'draft' | 'submitted' | 'reviewed';
  notes?: string;
}

export interface SavedGrading extends GradingSubmission {
  id: number;
  created_at: string;
  updated_at: string;
  created_by: number;
  updated_by?: number;
}

export interface GradingListResponse {
  gradings: SavedGrading[];
  total: number;
  page: number;
  size: number;
}

export interface GetGradingsRequest {
  protocol_id?: number;
  study_instance_uid?: string;
  patient_id?: string;
  grader_id?: string;
  status?: string;
  page?: number;
  size?: number;
}

export interface ApiResponse<T> {
  is_success: boolean;
  status: number;
  message: string;
  data: T;
}

class GradingService {
  private static instance: GradingService;
  private apiService: ApiService;

  private constructor() {
    this.apiService = ApiService.getInstance();
  }

  public static getInstance(): GradingService {
    if (!GradingService.instance) {
      GradingService.instance = new GradingService();
    }
    return GradingService.instance;
  }

  /**
   * Submit a new grading form
   */
  async submitGrading(gradingData: GradingSubmission): Promise<ApiResponse<SavedGrading>> {
    const baseUrl = this.apiService.getGradingServiceUrl();

    // For development: use mock data until API is deployed
    // Also use mock data if baseUrl is not configured
    if (!baseUrl || ENV.mode === 'Development' && !baseUrl.includes('deployed')) {
      await new Promise(resolve => setTimeout(resolve, 500));

      const mockResponse: SavedGrading = {
        id: Math.floor(Math.random() * 10000),
        ...gradingData,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        created_by: 1,
        status: gradingData.status || 'submitted',
      };

      return {
        is_success: true,
        status: 201,
        message: 'Grading submitted successfully',
        data: mockResponse,
      };
    }

    // Production: use real API
    const response = await this.apiService.post<SavedGrading>(
      '/gradings',
      gradingData,
      baseUrl
    );

    if (response.success && response.data) {
      return {
        is_success: true,
        status: 201,
        message: 'Grading submitted successfully',
        data: response.data,
      };
    }

    return {
      is_success: false,
      status: 500,
      message: response.error || 'Failed to submit grading',
      data: {} as SavedGrading,
    };
  }

  /**
   * Update an existing grading
   */
  async updateGrading(
    gradingId: number,
    gradingData: Partial<GradingSubmission>
  ): Promise<ApiResponse<SavedGrading>> {
    const baseUrl = this.apiService.getGradingServiceUrl();

    // For development: use mock data until API is deployed
    // Also use mock data if baseUrl is not configured
    if (!baseUrl || ENV.mode === 'Development' && !baseUrl.includes('deployed')) {
      await new Promise(resolve => setTimeout(resolve, 500));

      const mockResponse: SavedGrading = {
        id: gradingId,
        protocol_id: gradingData.protocol_id || 1,
        form_data: gradingData.form_data || {},
        created_at: new Date(Date.now() - 86400000).toISOString(),
        updated_at: new Date().toISOString(),
        created_by: 1,
        updated_by: 1,
        status: gradingData.status || 'submitted',
      };

      return {
        is_success: true,
        status: 200,
        message: 'Grading updated successfully',
        data: mockResponse,
      };
    }

    // Production: use real API
    const response = await this.apiService.put<SavedGrading>(
      `/gradings/${gradingId}`,
      gradingData,
      baseUrl
    );

    if (response.success && response.data) {
      return {
        is_success: true,
        status: 200,
        message: 'Grading updated successfully',
        data: response.data,
      };
    }

    return {
      is_success: false,
      status: 500,
      message: response.error || 'Failed to update grading',
      data: {} as SavedGrading,
    };
  }

  /**
   * Get gradings with optional filters
   */
  async getGradings(params: GetGradingsRequest = {}): Promise<ApiResponse<GradingListResponse>> {
    const baseUrl = this.apiService.getGradingServiceUrl();

    // For development: use mock data until API is deployed
    // Also use mock data if baseUrl is not configured
    if (!baseUrl || ENV.mode === 'Development' && !baseUrl.includes('deployed')) {
      await new Promise(resolve => setTimeout(resolve, 500));

      const mockGradings: SavedGrading[] = [
        {
          id: 1,
          protocol_id: 6,
          study_instance_uid: '1.2.840.113619.2.55.3.604688119.868.1234567890.1',
          patient_id: 'PAT001',
          grader_id: 'GRADER001',
          grading_date: '2025-10-07T10:30:00Z',
          form_data: {
            '11_12_field_23': '250',
            '11_12_field_28': 'Present',
            '11_12_field_30': ['CC', 'HRF'],
          },
          status: 'submitted',
          created_at: '2025-10-07T10:30:00Z',
          updated_at: '2025-10-07T10:30:00Z',
          created_by: 1,
        },
      ];

      const page = params.page || 1;
      const size = params.size || 10;

      return {
        is_success: true,
        status: 200,
        message: 'Gradings fetched successfully',
        data: {
          gradings: mockGradings,
          total: mockGradings.length,
          page,
          size,
        },
      };
    }

    // Production: use real API
    const response = await this.apiService.get<GradingListResponse>(
      '/gradings',
      params,
      baseUrl
    );

    if (response.success && response.data) {
      return {
        is_success: true,
        status: 200,
        message: 'Gradings fetched successfully',
        data: response.data,
      };
    }

    return {
      is_success: false,
      status: 500,
      message: response.error || 'Failed to fetch gradings',
      data: {
        gradings: [],
        total: 0,
        page: 1,
        size: 10,
      },
    };
  }

  /**
   * Get a single grading by ID
   */
  async getGradingById(gradingId: number): Promise<ApiResponse<SavedGrading>> {
    const baseUrl = this.apiService.getGradingServiceUrl();

    // For development: use mock data until API is deployed
    // Also use mock data if baseUrl is not configured
    if (!baseUrl || ENV.mode === 'Development' && !baseUrl.includes('deployed')) {
      await new Promise(resolve => setTimeout(resolve, 300));

      const mockGrading: SavedGrading = {
        id: gradingId,
        protocol_id: 6,
        study_instance_uid: '1.2.840.113619.2.55.3.604688119.868.1234567890.1',
        patient_id: 'PAT001',
        grader_id: 'GRADER001',
        grading_date: '2025-10-07T10:30:00Z',
        form_data: {
          '11_12_field_23': '250',
          '11_12_field_28': 'Present',
        },
        status: 'submitted',
        created_at: '2025-10-07T10:30:00Z',
        updated_at: '2025-10-07T10:30:00Z',
        created_by: 1,
      };

      return {
        is_success: true,
        status: 200,
        message: 'Grading fetched successfully',
        data: mockGrading,
      };
    }

    // Production: use real API
    const response = await this.apiService.get<SavedGrading>(
      `/gradings/${gradingId}`,
      undefined,
      baseUrl
    );

    if (response.success && response.data) {
      return {
        is_success: true,
        status: 200,
        message: 'Grading fetched successfully',
        data: response.data,
      };
    }

    return {
      is_success: false,
      status: 404,
      message: response.error || 'Grading not found',
      data: {} as SavedGrading,
    };
  }

  /**
   * Delete a grading
   */
  async deleteGrading(gradingId: number): Promise<ApiResponse<{ id: number }>> {
    const baseUrl = this.apiService.getGradingServiceUrl();

    // For development: use mock data until API is deployed
    // Also use mock data if baseUrl is not configured
    if (!baseUrl || ENV.mode === 'Development' && !baseUrl.includes('deployed')) {
      await new Promise(resolve => setTimeout(resolve, 300));

      return {
        is_success: true,
        status: 200,
        message: 'Grading deleted successfully',
        data: { id: gradingId },
      };
    }

    // Production: use real API
    const response = await this.apiService.delete<{ id: number }>(
      `/gradings/${gradingId}`,
      undefined,
      baseUrl
    );

    if (response.success && response.data) {
      return {
        is_success: true,
        status: 200,
        message: 'Grading deleted successfully',
        data: response.data,
      };
    }

    return {
      is_success: false,
      status: 500,
      message: response.error || 'Failed to delete grading',
      data: { id: gradingId },
    };
  }

  /**
   * Save as draft (partial save)
   */
  async saveDraft(gradingData: Partial<GradingSubmission>): Promise<ApiResponse<SavedGrading>> {
    return this.submitGrading({
      ...gradingData,
      status: 'draft',
    } as GradingSubmission);
  }
}

export default GradingService;
